import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import { ENV } from '@/constants';

const defaultParams = {
  backendUrl: ENV.VITE_BACKEND_URL,
};

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: ENV.VITE_API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.get(url, {
      ...config,
      params: { ...defaultParams, ...config?.params },
    });
    return response.data;
  }

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.post(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.put(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.delete(url, config);
    return response.data;
  }
}

// Create and export a singleton instance
export const apiClient = new ApiClient();

// Orval mutator function for React Query integration
export const orvalMutator = <T>(config: AxiosRequestConfig): Promise<T> => {
  const { url, method = 'get', data, ...rest } = config;

  switch (method.toLowerCase()) {
    case 'get':
      return apiClient.get<T>(url!, rest);
    case 'post':
      return apiClient.post<T>(url!, data, rest);
    case 'put':
      return apiClient.put<T>(url!, data, rest);
    case 'patch':
      return apiClient.put<T>(url!, data, rest); // Using put for patch
    case 'delete':
      return apiClient.delete<T>(url!, rest);
    default:
      throw new Error(`Unsupported HTTP method: ${method}`);
  }
};

// Export the class for testing or multiple instances if needed
export { ApiClient };
